import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const transformations = [
  {
    name: 'Buddy',
    before: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/cafu8ngr_1000017327.jpeg',
    after: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/2026gnvz_IMG_7362.jpeg',
  },
  {
    name: 'Nola',
    before: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/kp8b9zeo_IMG_7340.jpeg',
    after: 'https://customer-assets.emergentagent.com/job_taupe-wellness/artifacts/d9n025al_IMG_7376.jpeg',
    beforePosition: 'center 50%',
    afterPosition: 'center 50%',
  }
];

function BeforeAfterSlider({ before, after, name, objectPosition, beforePosition, afterPosition }) {
  const { t } = useTranslation();
  const bPos = beforePosition || objectPosition || 'center center';
  const aPos = afterPosition || objectPosition || 'center center';
  const [sliderPos, setSliderPos] = useState(50);
  const containerRef = useRef(null);
  const isDragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percent = Math.max(2, Math.min(98, (x / rect.width) * 100));
    setSliderPos(percent);
  }, []);

  const handleStart = (e) => {
    isDragging.current = true;
    e.preventDefault();
  };

  const handleMouseMove = (e) => { if (isDragging.current) updatePosition(e.clientX); };
  const handleTouchMove = (e) => { if (isDragging.current) updatePosition(e.touches[0].clientX); };
  const handleClick = (e) => { updatePosition(e.clientX); };

  useEffect(() => {
    const up = () => { isDragging.current = false; };
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        paddingBottom: '120%',
        borderRadius: '24px',
        overflow: 'hidden',
        cursor: 'ew-resize',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none',
        boxShadow: '0 12px 40px rgba(59, 48, 45, 0.12)',
      }}
      onMouseMove={handleMouseMove}
      onTouchMove={handleTouchMove}
      onClick={handleClick}
    >
      <img src={after} alt={t('transformations.altAfter', { name })} draggable={false}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: aPos, pointerEvents: 'none' }}
      />
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', clipPath: `inset(0 ${100 - sliderPos}% 0 0)`, zIndex: 1 }}>
        <img src={before} alt={t('transformations.altBefore', { name })} draggable={false}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: bPos, pointerEvents: 'none' }}
        />
      </div>
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`, width: '3px',
        background: 'white', zIndex: 3, transform: 'translateX(-50%)', boxShadow: '0 0 8px rgba(0,0,0,0.3)',
      }}>
        <div onMouseDown={handleStart} onTouchStart={handleStart}
          style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: '48px', height: '48px', borderRadius: '50%', background: '#9a7465',
            border: '3px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'ew-resize', boxShadow: '0 4px 16px rgba(0,0,0,0.3)', touchAction: 'none', zIndex: 4,
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 5L3 12L8 19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 5L21 12L16 19" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
      <div style={{
        position: 'absolute', top: '1rem', left: '1rem', zIndex: 4,
        padding: '0.35rem 1rem', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
        color: 'white', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em',
        textTransform: 'uppercase', borderRadius: '50px', pointerEvents: 'none',
      }} data-testid="slider-before-label">{t('transformations.before')}</div>
      <div style={{
        position: 'absolute', top: '1rem', right: '1rem', zIndex: 4,
        padding: '0.35rem 1rem', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
        color: 'white', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.1em',
        textTransform: 'uppercase', borderRadius: '50px', pointerEvents: 'none',
      }} data-testid="slider-after-label">{t('transformations.after')}</div>
    </div>
  );
}

function TransformationSlider() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const active = transformations[activeIndex];

  return (
    <section data-testid="transformation-section" style={{ maxWidth: '500px', margin: '0 auto', padding: '80px 24px' }}>
      <div className="section-header">
        <span className="section-label">{t('transformations.label')}</span>
        <h2 className="section-title">{t('transformations.title')}</h2>
      </div>

      {/* Main Slider */}
      <BeforeAfterSlider key={activeIndex} before={active.before} after={active.after} name={active.name} objectPosition={active.objectPosition} beforePosition={active.beforePosition} afterPosition={active.afterPosition} />
      <p style={{
        textAlign: 'center', marginTop: '0.75rem',
        fontFamily: "'Cormorant Garamond', 'Playfair Display', serif",
        fontSize: '1.25rem', fontWeight: 500, color: '#3b302d', letterSpacing: '0.02em',
      }}>{active.name}</p>

      {/* Thumbnail Library */}
      <div style={{
        display: 'flex', justifyContent: 'center', gap: '1rem',
        marginTop: '1.5rem', flexWrap: 'wrap',
      }}>
        {transformations.map((t, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            data-testid={`thumb-${t.name}`}
            style={{
              cursor: 'pointer', border: 'none', background: 'none', padding: 0,
              textAlign: 'center', outline: 'none',
              opacity: i === activeIndex ? 1 : 0.5,
              transform: i === activeIndex ? 'scale(1)' : 'scale(0.95)',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{
              width: '80px', height: '80px', borderRadius: '16px', overflow: 'hidden',
              border: i === activeIndex ? '3px solid #9a7465' : '3px solid transparent',
              transition: 'border-color 0.3s ease',
            }}>
              <img
                src={t.after}
                alt={t.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%' }}
              />
            </div>
            <p style={{
              marginTop: '0.4rem', fontSize: '0.75rem', fontWeight: 600,
              color: i === activeIndex ? '#9a7465' : '#3b302d',
              letterSpacing: '0.05em', transition: 'color 0.3s ease',
            }}>{t.name}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

export default TransformationSlider;
